import React, { useState } from "react";
import { parseScale, scaleLabel, toInt } from "../../pages/UserTest/testUtils";

/**
 * RatingQuestion
 * Renders the 1-10 scale as large circular buttons. Hovering previews the
 * label for that value; selecting animates the circle and calls onSelect,
 * which the page uses to save + auto-advance to the next question.
 */
const RatingQuestion = ({ question, selectedOptionId, onSelect }) => {
  const [previewValue, setPreviewValue] = useState(null);

  const options = (question.answerOptions || []).slice().sort((a, b) => {
    const aNum = parseScale(a.answerText);
    const bNum = parseScale(b.answerText);
    if (aNum == null && bNum == null) return 0;
    if (aNum == null) return 1;
    if (bNum == null) return -1;
    return aNum - bNum;
  });

  const selectedValue = (() => {
    const match = options.find(
      (o) => toInt(o.answerOptionId) === toInt(selectedOptionId),
    );
    return match ? parseScale(match.answerText) : null;
  })();

  const displayValue = previewValue ?? selectedValue;

  return (
    <div className="rating-question">
      <div
        className="rating-track"
        role="radiogroup"
        aria-label={question.questionText}
      >
        {options.map((option) => {
          const numericValue = parseScale(option.answerText);
          const optId = toInt(option.answerOptionId);
          const isSelected = optId != null && toInt(selectedOptionId) === optId;
          const isPreview =
            previewValue != null &&
            numericValue === previewValue &&
            !isSelected;

          return (
            <button
              key={option.answerOptionId}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${numericValue} — ${scaleLabel(numericValue)}`}
              className={[
                "rating-circle",
                isSelected ? "selected" : "",
                isPreview ? "preview" : "",
              ]
                .join(" ")
                .trim()}
              onMouseEnter={() => setPreviewValue(numericValue)}
              onMouseLeave={() => setPreviewValue(null)}
              onFocus={() => setPreviewValue(numericValue)}
              onBlur={() => setPreviewValue(null)}
              onClick={() => optId != null && onSelect(optId)}
            >
              {numericValue}
            </button>
          );
        })}
      </div>

      <div className="rating-labels">
        <span>Strongly Disagree</span>
        <span>Strongly Agree</span>
      </div>

      <div className="rating-caption" aria-live="polite">
        {displayValue != null ? scaleLabel(displayValue) : ""}
      </div>
    </div>
  );
};

export default RatingQuestion;
